import { Button } from "@/components/ui/button";
import { auth, db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { sendOfferV2, type OfferType } from "@/data/offersV2";
import { toastCheeky } from "@/lib/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import UserChip from "@/components/UserChip";
import { createPortal } from "react-dom";

type CardContext = {
  cardId?: string;
  cardOwnerUid?: string;
  name?: string;
  scryfall_id?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  toUid: string;
  toUsername?: string;
  card?: CardContext;
};

export default function OfferModal({ open, onClose, toUid, toUsername, card }: Props) {
  const myUid = auth.currentUser?.uid ?? null;

  // UI state
  const [offerType, setOfferType] = useState<OfferType>("trade");
  const [price, setPrice] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [displayTo, setDisplayTo] = useState<string>(toUsername || "");
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Prevent body scrolling when modal is open (mobile-friendly)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Resolve display username if not passed
  useEffect(() => {
    (async () => {
      if (displayTo || !toUid) return;
      try {
        const u = await getDoc(doc(db, "users", toUid));
        if (u.exists()) {
          const v = u.data() as any;
          setDisplayTo(v?.username || v?.display_name || toUid.slice(0, 6));
        }
      } catch {
        // noop
      }
    })();
  }, [toUid, displayTo]);

  const priceNum = useMemo(() => {
    const n = Number(price);
    return Number.isFinite(n) ? n : null;
  }, [price]);

  const priceInvalid = offerType === "purchase" && (priceNum == null || priceNum < 0);

  async function onSubmit() {
    setErr(null);
    if (!myUid) {
      setErr("Please sign in to send offers.");
      return;
    }
    if (!toUid || toUid === myUid) {
      setErr("Invalid recipient.");
      return;
    }
    if (priceInvalid) {
      setErr("Please enter a valid price.");
      return;
    }

    setSubmitting(true);
    try {
      await sendOfferV2({
        toUid,
        cardId: card?.cardId,
        cardOwnerUid: card?.cardOwnerUid ?? toUid,
        cardName: card?.name,
        scryfall_id: card?.scryfall_id,
        offerType,
        offeredPrice: offerType === "purchase" ? priceNum : null,
        message: message.trim() || null,
      });
      toastCheeky("offerSent");
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Could not send offer.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true">
      {/* scrim */}
      <button
        className="absolute inset-0 bg-black/60"
        aria-label="Close"
        onClick={onClose}
      />

      {/* dialog: bottom sheet on mobile, centered on md+ */}
      <div className="absolute inset-0 grid place-items-end md:place-items-center md:p-4 z-[10000]">
        <div
          ref={containerRef}
          className="relative w-full md:w-[min(480px,95vw)] max-h-[100svh] md:max-h-[min(90svh,720px)] overflow-hidden md:rounded-lg rounded-t-2xl border border-slate-700 bg-gray-900 shadow-2xl flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
        >
          {/* drag handle on mobile */}
          <div className="md:hidden grid place-items-center pt-2">
            <div className="h-1.5 w-10 rounded-full bg-white/15" />
          </div>

          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-800 px-4 py-3 bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-gray-900/60">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-white/80 text-sm shrink-0">Offer to</span>
              <UserChip
                uid={toUid}
                username={displayTo || toUid?.slice(0, 6)}
                size="sm"
                className="max-w-[60vw] md:max-w-none"
                onViewProfile={() => {
                  // Wire this to your router as desired
                  // Example: router.push(`/u/${displayTo || toUid}`);
                  window.dispatchEvent(new CustomEvent("userchip:view-profile", { detail: { uid: toUid } }));
                }}
                onSendMessage={() => {
                  window.dispatchEvent(new CustomEvent("userchip:send-message", { detail: { uid: toUid } }));
                }}
                onFollow={() => {
                  window.dispatchEvent(new CustomEvent("userchip:follow", { detail: { uid: toUid } }));
                }}
              />
            </div>
            <button
              type="button"
              className="p-1 rounded hover:bg-white/10 text-white/80"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4">
            {card?.name && (
              <div className="text-sm text-white/90">
                Card: <span className="font-semibold">{card.name}</span>
              </div>
            )}

            {/* Type */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={[
                  "px-3 py-1 text-xs rounded-md border transition",
                  offerType === "trade"
                    ? "bg-white/10 border-white/20 text-white"
                    : "bg-black/30 border-white/10 text-white/80 hover:bg-white/10",
                ].join(" ")}
                onClick={() => setOfferType("trade")}
              >
                Trade
              </button>
              <button
                type="button"
                className={[
                  "px-3 py-1 text-xs rounded-md border transition",
                  offerType === "purchase"
                    ? "bg-white/10 border-white/20 text-white"
                    : "bg-black/30 border-white/10 text-white/80 hover:bg-white/10",
                ].join(" ")}
                onClick={() => setOfferType("purchase")}
              >
                Purchase
              </button>
            </div>

            {/* Price if purchase */}
            {offerType === "purchase" && (
              <label className="block">
                <span className="block text-xs text-white/70 mb-1">Offer price (USD)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  className={[
                    "w-full rounded border bg-black/30 px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:ring-2",
                    priceInvalid ? "border-amber-400/60 focus:ring-amber-400/30" : "border-white/10 focus:ring-white/20",
                  ].join(" ")}
                  placeholder="20.00"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
                {priceInvalid && (
                  <div className="mt-1 text-amber-300 text-xs">Enter a valid non-negative amount.</div>
                )}
              </label>
            )}

            {/* Message */}
            <label className="block">
              <span className="block text-xs text-white/70 mb-1">Message (optional)</span>
              <textarea
                rows={4}
                className="w-full rounded border border-white/10 bg-black/30 px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/20"
                placeholder="Hey! I’m interested in this card…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>

            {err && <div className="text-amber-300 text-sm">{err}</div>}
          </div>

          <div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 border-t border-slate-800 px-4 py-3 bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-gray-900/60">
            <Button variant="ghost" className="text-white/90" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="btn-pixel btn-pixel-primary"
              onClick={() => void onSubmit()}
              disabled={submitting || !myUid || !toUid || toUid === myUid || priceInvalid}
            >
              {submitting ? "Sending…" : "Send offer"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}
